import { useState } from "react";
import axios from "axios";
import { CheckCircle2, GitMerge, Lock, MessageSquare, Trash2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";

/**
 * EG7 (CA4, opdracht Frank 1 november 2026): review en samenvoegen in het gesprek van een pull request.
 *
 * Naar GitHub: opmerkingen (ook bij een regel van een bestand), Goedkeuren of Wijzigingen vragen, en onderaan de knop
 * Samenvoegen. Die knop voegt niets samen: hij toont eerst de kaart (wat er gebeurt, of het kan, met een eenmalig token
 * van de server) en pas Bevestig op die kaart voegt samen, met een merge commit. Merge naar main blijft altijd bij Frank
 * na zijn klik (opdracht 1 november); de backend weigert zonder het token van deze kaart.
 */
export default function PullSamenvoegen({ repo, pull, onVernieuwd, onTakWeg = () => {} }) {
  const { t } = useTaal();
  const [tekst, setTekst] = useState("");
  const [pad, setPad] = useState("");
  const [regel, setRegel] = useState("");
  const [kaart, setKaart] = useState(null);
  const [bezig, setBezig] = useState(false);
  // GL225-3b: na samenvoegen de tak weg, alleen na een tweede klik; nooit de hoofdtak (de backend weigert ook).
  const [takWeg, setTakWeg] = useState(false);
  const basis = `${API}/repos/${repo.id}/pulls/${pull.nummer}`;

  const fout = (e, standaard) => {
    const d = e?.response?.data?.detail;
    toast.error(typeof d === "string" ? t(d) : standaard);
  };
  const ververs = async () => onVernieuwd((await axios.get(basis)).data);

  async function plaats(e) {
    e.preventDefault();
    try {
      await axios.post(`${basis}/opmerkingen`, { tekst, pad, regel: pad && regel ? Number(regel) : null });
      setTekst("");
      setRegel("");
      await ververs();
    } catch (err) {
      fout(err, t("De opmerking kon niet worden geplaatst."));
    }
  }

  async function beoordeel(soort) {
    try {
      await axios.post(`${basis}/beoordeling`, { soort, tekst: "" });
      await ververs();
    } catch (err) {
      fout(err, t("De beoordeling kon niet worden opgeslagen."));
    }
  }

  async function toon() {
    try {
      setKaart((await axios.post(`${basis}/samenvoegen/toon`)).data);
    } catch (err) {
      fout(err, t("De kaart Samenvoegen kon niet worden getoond."));
    }
  }

  async function bevestig() {
    setBezig(true);
    try {
      await axios.post(`${basis}/samenvoegen`, { token: kaart.token });
      setKaart(null);
      toast.success(t("Samengevoegd met een merge commit."));
      await ververs();
    } catch (err) {
      setKaart(null);
      fout(err, t("Samenvoegen lukte niet."));
    } finally {
      setBezig(false);
    }
  }

  async function verwijderTak() {
    if (!takWeg) return setTakWeg(true);
    setTakWeg(false);
    try {
      await axios.delete(`${API}/repos/${repo.id}/takken`, { params: { naam: pull.van } });
      toast.success(t("Tak {naam} verwijderd.", { naam: pull.van }));
      onTakWeg(pull.van);
      await ververs();
    } catch (err) {
      fout(err, t("De tak kon niet worden verwijderd."));
    }
  }

  const laatste = (pull.beoordelingen || []).slice(-1)[0];
  return (
    <div className="space-y-2" data-testid="pull-review">
      {(pull.opmerkingen || []).length > 0 && (
        <ul className="space-y-1" data-testid="pull-opmerkingen">
          {pull.opmerkingen.map((o) => (
            <li key={o.id} className="rounded-lg border p-2 text-sm">
              {o.pad && <code className="text-xs text-muted-foreground">{o.pad}{o.regel ? `:${o.regel}` : ""}</code>}
              <p className="whitespace-pre-wrap break-words">{o.tekst}</p>
            </li>
          ))}
        </ul>
      )}
      {/* Glasronde 0.9.234 punt 3: in een teamrepo per persoon wie beoordeelde, en of het telt voor de vier ogen. */}
      {pull.beoordelingen_per_persoon?.length > 0 && (
        <ul className="space-y-0.5" data-testid="pull-beoordelingen">
          {pull.beoordelingen_per_persoon.map((b) => (
            <li key={b.door} className="flex flex-wrap items-center gap-1 text-xs" data-testid="pull-beoordeling-persoon">
              {b.soort === "goedgekeurd"
                ? <><CheckCircle2 className={`h-3.5 w-3.5 ${b.telt ? "text-emerald-600" : "text-muted-foreground"}`} />{t("Goedgekeurd door {naam}", { naam: b.door_naam })}</>
                : <><XCircle className="h-3.5 w-3.5 text-amber-600" />{t("Wijzigingen gevraagd door {naam}", { naam: b.door_naam })}</>}
              {b.telt_niet === "maker" && (
                <span className="text-amber-700" data-testid="telt-niet-maker">{t("telt niet: goedkeuring van een ander teamlid nodig")}</span>
              )}
              {b.telt_niet === "oude_stand" && (
                <span className="text-amber-700" data-testid="telt-niet-oude-stand">{t("telt niet: gegeven op een eerdere stand van de tak")}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {laatste && !pull.beoordelingen_per_persoon && (
        <p className="flex items-center gap-1 text-xs" data-testid="pull-beoordeling">
          {laatste.soort === "goedgekeurd"
            ? <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />{t("Goedgekeurd")}</>
            : <><XCircle className="h-3.5 w-3.5 text-amber-600" />{t("Wijzigingen gevraagd")}</>}
        </p>
      )}
      {pull.status === "samengevoegd" && pull.merge && (
        <p className="flex items-center gap-1 text-sm text-violet-700" data-testid="pull-samengevoegd">
          <GitMerge className="h-4 w-4" />{t("Samengevoegd met merge commit {sha}.", { sha: pull.merge.kort })}
        </p>
      )}
      {pull.status === "samengevoegd" && pull.van_bestaat && pull.van !== (repo.default_branch || "main") && (
        <Button type="button" size="sm" variant={takWeg ? "destructive" : "outline"} className="tap-target"
                onClick={verwijderTak} data-testid="pull-tak-verwijderen">
          <Trash2 className="h-4 w-4 mr-1" />
          {takWeg ? t("Zeker weten?") : t("Verwijder tak {naam}", { naam: pull.van })}
        </Button>
      )}
      {pull.status === "open" && (
        <>
          <form onSubmit={plaats} className="space-y-1 rounded-lg border p-2" data-testid="opmerking-vorm">
            <div className="flex flex-wrap gap-2 text-xs">
              <select value={pad} onChange={(e) => setPad(e.target.value)} aria-label={t("Bij welk bestand")}
                      className="rounded-md border bg-transparent px-1 py-1" data-testid="opmerking-pad">
                <option value="">{t("Bij de hele pull request")}</option>
                {(pull.bestanden || []).map((b) => <option key={b.pad} value={b.pad}>{b.pad}</option>)}
              </select>
              {pad && (
                <input type="number" min="1" value={regel} onChange={(e) => setRegel(e.target.value)} placeholder={t("regel")}
                       aria-label={t("Regelnummer")} className="w-20 rounded-md border bg-transparent px-1 py-1" data-testid="opmerking-regel" />
              )}
            </div>
            <textarea value={tekst} onChange={(e) => setTekst(e.target.value)} placeholder={t("Schrijf een opmerking")}
                      aria-label={t("Schrijf een opmerking")} className="min-h-[60px] w-full rounded-md border bg-transparent px-2 py-1 text-sm"
                      data-testid="opmerking-tekst" />
            <Button type="submit" size="sm" variant="outline" className="tap-target" disabled={!tekst.trim()} data-testid="opmerking-plaats">
              <MessageSquare className="h-4 w-4 mr-1" />{t("Opmerking plaatsen")}
            </Button>
          </form>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="outline" className="tap-target" onClick={() => beoordeel("goedgekeurd")}
                    data-testid="pull-goedkeuren">{t("Goedkeuren")}</Button>
            <Button type="button" size="sm" variant="outline" className="tap-target" onClick={() => beoordeel("wijzigingen")}
                    data-testid="pull-wijzigingen">{t("Wijzigingen vragen")}</Button>
            <Button type="button" size="sm" className="tap-target ml-auto" onClick={toon} disabled={bezig || Boolean(kaart)}
                    data-testid="pull-samenvoegen">
              <GitMerge className="h-4 w-4 mr-1" />{t("Samenvoegen")}
            </Button>
          </div>
          {kaart && (
            <section className="space-y-2 rounded-lg border border-violet-500/40 p-3" data-testid="samenvoeg-kaart">
              <p className="text-sm">
                {t("Voeg {van} samen in {naar} met een merge commit.", { van: kaart.van, naar: kaart.naar })}
              </p>
              {kaart.sluit_issues?.length > 0 && (
                <p className="text-xs" data-testid="samenvoeg-sluit">
                  {t("Na samenvoegen sluit: {issues}.", { issues: kaart.sluit_issues.map((n) => `#${n}`).join(", ") })}
                </p>
              )}
              {kaart.beschermd && (
                <p className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Lock className="h-3 w-3" aria-hidden="true" />{t("{naar} is beschermd: dit is de enige weg waarlangs hij verandert.", { naar: kaart.naar })}
                </p>
              )}
              {kaart.wacht_op_goedkeuring && (
                <p className="text-sm text-amber-700" data-testid="samenvoeg-vier-ogen">
                  {t("Dit is een teamrepo: naar {naar} gaat een pull request pas na goedkeuring door een ander teamlid dan de maker, op de stand van nu.", { naar: kaart.naar })}
                </p>
              )}
              {!kaart.kan && kaart.conflicten?.length > 0 && (
                <p className="text-sm text-red-600" data-testid="samenvoeg-conflict">
                  {t("Er is een conflict in {bestanden}. Los het op in de tak en probeer opnieuw.", { bestanden: kaart.conflicten.join(", ") })}
                </p>
              )}
              <div className="flex gap-2">
                {kaart.kan && (
                  <Button type="button" size="sm" className="tap-target" disabled={bezig || !kaart.token} onClick={bevestig}
                          data-testid="samenvoeg-bevestig">{t("Bevestig")}</Button>
                )}
                <Button type="button" size="sm" variant="outline" className="tap-target" onClick={() => setKaart(null)}
                        data-testid="samenvoeg-annuleer">{t("Annuleer")}</Button>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
